import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CodedError } from '@ebu/contracts';
import type { StartChoice } from '@ebu/adaptive-engine';
import { LocalStore } from '../../adapters/local-store/local-store';
import type { AttemptStoreRecord } from '../../adapters/local-store/schema';
import { useDiagnostics } from '../../diagnostics/context';
import type { GuestServices } from '../guest/guest-state';
import { useGuest } from '../guest/guest-state';
import { evidenceFor, type LearningAttempt } from './learning-records';

/**
 * Challenge attempts and the child's choices for the Lab (Phase 7, ADR 0013). Everything lives in
 * memory by default; only after the child answers "Yes, save it" are attempts and evidence written
 * to this browser (IndexedDB) — never to the server. "Clear my data" empties it (dataEpoch).
 * A fictional sample learner (Phase 9) is its own workspace, cloned with saving already on.
 */

export type { LearningAttempt } from './learning-records';
export type NewAttempt = Omit<LearningAttempt, 'id' | 'endedAt'>;

export interface LearningState {
  attempts: readonly LearningAttempt[];
  /** null = not asked yet; false = "No thanks"; true = saved on this browser. */
  saveProgress: boolean | null;
  startChoice: StartChoice | null;
  /** Saving failed (LEARN-EVIDENCE-001); play continues in memory. */
  saveFailed: boolean;
  /** Writes still running; "saved" is only said once they have finished. */
  saving: boolean;
  record: (attempt: NewAttempt) => void;
  chooseSave: (save: boolean) => void;
  chooseStart: (choice: StartChoice) => void;
}

interface Held {
  /** Which data this is: "Clear my data" epoch plus the workspace (own or a sample learner). */
  epoch: string;
  attempts: LearningAttempt[];
  saveProgress: boolean | null;
  startChoice: StartChoice | null;
  saveFailed: boolean;
  pending: number;
}

const fresh = (epoch: string): Held => ({
  epoch,
  attempts: [],
  saveProgress: null,
  startChoice: null,
  saveFailed: false,
  pending: 0,
});

const LearningContext = createContext<LearningState | null>(null);

export function useLearning(): LearningState {
  const state = useContext(LearningContext);
  if (!state) throw new Error('useLearning must be used inside <LearningProvider>');
  return state;
}

function withoutWorkspace(record: AttemptStoreRecord): LearningAttempt {
  const { workspaceId, ...attempt } = record;
  void workspaceId; // attempts in memory are not tied to a workspace until they are saved
  return attempt;
}

function randomId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `attempt-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export function LearningProvider({
  services,
  children,
}: {
  services: GuestServices;
  children: ReactNode;
}) {
  const { locale, dataEpoch, activeSample } = useGuest();
  const logger = useDiagnostics();
  const store = useRef<LocalStore | null>(null);
  const saved = useRef(new Set<string>());
  const sampleId = activeSample?.id ?? null;
  const epoch = `${dataEpoch}|${sampleId ?? 'own'}`;
  const [held, setHeld] = useState<Held>(() => fresh(epoch));
  // After "Clear my data" or a workspace switch, everything held in memory is dropped too.
  const current = held.epoch === epoch ? held : fresh(epoch);
  const latest = useRef({ current, locale, sampleId });
  useEffect(() => {
    latest.current = { current, locale, sampleId };
  });

  const update = useCallback(
    (change: (h: Held) => Partial<Held>) =>
      setHeld((prev) => {
        const base = prev.epoch === epoch ? prev : fresh(epoch);
        return { ...base, ...change(base) };
      }),
    [epoch],
  );

  /** The workspace attempts belong to: the sample learner in use, else the guest's own. */
  const target = useCallback(async (opened: LocalStore) => {
    const id = latest.current.sampleId;
    return id ?? (await opened.getOrCreateGuestWorkspace(latest.current.locale)).id;
  }, []);

  const fail = useCallback(
    (message: string, error: unknown) => {
      logger.warn('learning', message, {
        code: 'LEARN-EVIDENCE-001',
        origin: 'apps/web/src/features/discovery-game/learning-state.tsx:LearningProvider',
        context: {
          quotaExceeded:
            error instanceof CodedError ? error.context['quotaExceeded'] === true : false,
          err: error,
        },
      });
      update(() => ({ saveFailed: true }));
    },
    [logger, update],
  );

  // Open the local store and, if the child chose to save, load what was saved before.
  useEffect(() => {
    let cancelled = false;
    saved.current = new Set();
    void (async () => {
      try {
        const opened = await LocalStore.open({ idbFactory: services.idbFactory });
        if (cancelled) {
          opened.close();
          return;
        }
        store.current = opened;
        const ws = sampleId
          ? await opened.getWorkspace(sampleId)
          : await opened.getGuestWorkspace();
        if (!ws || cancelled) return;
        const loaded = ws.saveProgress
          ? (await opened.listAttempts(ws.id)).attempts.map(withoutWorkspace)
          : [];
        if (cancelled) return;
        for (const a of loaded) saved.current.add(a.id);
        update((h) => ({
          saveProgress: h.saveProgress ?? ws.saveProgress,
          startChoice: h.startChoice ?? ws.startChoice,
          attempts: [...loaded, ...h.attempts.filter((a) => !saved.current.has(a.id))],
        }));
      } catch (error) {
        // No storage (private browsing, blocked site data): progress simply stays in memory.
        if (!cancelled) {
          logger.info('learning', 'Local storage unavailable; progress kept in memory only', {
            context: { reason: error instanceof CodedError ? error.code : 'unknown' },
          });
        }
      }
    })();
    return () => {
      cancelled = true;
      store.current?.close();
      store.current = null;
    };
  }, [services.idbFactory, dataEpoch, sampleId, logger, update]);

  /** Runs a storage write while counting it as pending (so the UI never says "saved" too early). */
  const tracked = useCallback(
    async (work: () => Promise<void>) => {
      update((h) => ({ pending: h.pending + 1 }));
      try {
        await work();
      } finally {
        update((h) => ({ pending: Math.max(0, h.pending - 1) }));
      }
    },
    [update],
  );

  const persist = useCallback(
    async (history: readonly LearningAttempt[]) => {
      const opened = store.current;
      if (!opened) throw new CodedError('LEARN-EVIDENCE-001', 'Local storage is not available');
      const workspaceId = await target(opened);
      for (const attempt of history) {
        if (saved.current.has(attempt.id)) continue;
        await opened.saveLearning({ ...attempt, workspaceId }, [
          evidenceFor(workspaceId, attempt, history),
        ]);
        saved.current.add(attempt.id);
      }
    },
    [target],
  );

  const record = useCallback(
    (attempt: NewAttempt) => {
      const full: LearningAttempt = {
        ...attempt,
        id: randomId(),
        endedAt: new Date().toISOString(),
      };
      const history = [...latest.current.current.attempts, full];
      update(() => ({ attempts: history }));
      if (latest.current.current.saveProgress === true) {
        persist(history).catch((error: unknown) =>
          fail('Challenge attempt could not be saved', error),
        );
      }
    },
    [persist, update, fail],
  );

  const chooseSave = useCallback(
    (save: boolean) => {
      update(() => ({ saveProgress: save, saveFailed: false }));
      const { attempts, startChoice } = latest.current.current;
      void tracked(async () => {
        try {
          const opened = store.current;
          if (!opened) throw new CodedError('LEARN-EVIDENCE-001', 'Local storage is not available');
          const workspaceId = await target(opened);
          await opened.updateWorkspace(workspaceId, {
            saveProgress: save,
            startChoice: save ? startChoice : null,
          });
          if (save) await persist(attempts);
          else {
            await opened.deleteLearning(workspaceId);
            saved.current = new Set();
          }
        } catch (error) {
          fail(save ? 'Progress could not be saved' : 'Saved progress could not be removed', error);
        }
      });
    },
    [persist, tracked, update, fail, target],
  );

  const chooseStart = useCallback(
    (choice: StartChoice) => {
      update(() => ({ startChoice: choice }));
      if (latest.current.current.saveProgress !== true) return;
      void (async () => {
        try {
          const opened = store.current;
          if (!opened) return;
          await opened.updateWorkspace(await target(opened), { startChoice: choice });
        } catch (error) {
          fail('Start choice could not be saved', error);
        }
      })();
    },
    [update, fail, target],
  );

  const value = useMemo<LearningState>(
    () => ({
      attempts: current.attempts,
      saveProgress: current.saveProgress,
      startChoice: current.startChoice,
      saveFailed: current.saveFailed,
      saving: current.pending > 0,
      record,
      chooseSave,
      chooseStart,
    }),
    [current, record, chooseSave, chooseStart],
  );
  return <LearningContext.Provider value={value}>{children}</LearningContext.Provider>;
}
